import { memo, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { appStore } from '~/mobx/appStore';

export enum MARKETING_EDIT_SKELETONS {
  PROMOCODE,
  PROMOBANNER,
  CASHBACK,
}

interface ISkeletonSize {
  left: number;
  right: number;
}

interface ISkeleton {
  size: ISkeletonSize;
  restriction?: boolean;
}

interface IProps {
  type: MARKETING_EDIT_SKELETONS;
}

// promocode and cashback
const skeletonSm: ISkeletonSize = { left: 130, right: 22 };
const skeletonNm: ISkeletonSize = { left: 150, right: 42 };
const skeletonLg: ISkeletonSize = { left: 150, right: 77 };
// promobanner
const skeletonPromoBanner: ISkeletonSize = { left: 150, right: 49 };

const EditSkeleton: React.FC<ISkeleton> = (props: ISkeleton): JSX.Element | null => {
  if (props.restriction === false) return null;

  return (
    <div className="d-flex flex-column mt-6">
      <Skeleton height={21.6} width={props.size.left} />
      <Skeleton className="mt-2" height={46} width={String(props.size.right) + '%'} />
    </div>
  );
};

const MarketingEditSkeletons: React.FC<IProps> = (props: IProps): JSX.Element => {
  const isAdvancedSettingsEnabled: boolean = appStore.isAddonsAvailable.promoCode;

  const promocodeSkeletons: ISkeleton[] = useMemo(() => {
    return [
      { size: skeletonNm },
      { size: skeletonLg },
      { size: skeletonLg },
      { size: skeletonLg, restriction: isAdvancedSettingsEnabled },
      { size: skeletonLg, restriction: isAdvancedSettingsEnabled },
      { size: skeletonNm },
      { size: skeletonSm, restriction: isAdvancedSettingsEnabled },
      { size: skeletonSm, restriction: isAdvancedSettingsEnabled },
    ];
  }, [isAdvancedSettingsEnabled]);

  const promobannerSkeletons: ISkeleton[] = useMemo(() => {
    return [{ size: skeletonPromoBanner }, { size: skeletonPromoBanner }, { size: skeletonPromoBanner }];
  }, []);

  const cashbackSkeletons: ISkeleton[] = useMemo(() => {
    return [{ size: skeletonNm }, { size: skeletonNm }, { size: skeletonLg }, { size: skeletonSm }, { size: skeletonNm }];
  }, []);

  const items: ISkeleton[] = useMemo(() => {
    switch (true) {
      case props.type === MARKETING_EDIT_SKELETONS.PROMOCODE:
        return promocodeSkeletons;
      case props.type === MARKETING_EDIT_SKELETONS.PROMOBANNER:
        return promobannerSkeletons;
      default:
        return cashbackSkeletons;
    }
  }, [props.type, promocodeSkeletons, promobannerSkeletons, cashbackSkeletons]);

  return (
    <>
      {items.map((item, index) => (
        <EditSkeleton key={index} size={item.size} restriction={item.restriction} />
      ))}
    </>
  );
};

export default memo(MarketingEditSkeletons);
